import Ably from 'ably'
import { useEffect, useRef } from 'react'

export type RealtimeEventName =
  | 'new-message'
  | 'processing-reply-finished'
  | 'processing-user-audio-test-finished'
  | 'processing-test-result-finished'

const REALTIME_EVENTS: RealtimeEventName[] = [
  'new-message',
  'processing-reply-finished',
  'processing-user-audio-test-finished',
  'processing-test-result-finished'
]

export type RealtimeMessage = {
  name: RealtimeEventName
  data: unknown
}

export function useRealtimeChannel(
  channelName: string | null,
  token: string | null,
  onMessage: (message: RealtimeMessage) => void
): void {
  const callbackRef = useRef(onMessage)
  const clientRef = useRef<Ably.Realtime>(undefined)

  useEffect(() => {
    if (token && channelName) {
      clientRef.current = new Ably.Realtime({
        clientId: channelName,
        autoConnect: true,
        authCallback: async (_tokenParams, callback) => {
          callback(null, token)
        }
      })
    }

    return () => {
      if (clientRef.current) {
        clientRef.current.close()
        clientRef.current = undefined
      }
    }
  }, [channelName, token])

  useEffect(() => {
    if (!clientRef.current) return

    const setupChannel = async () => {
      await clientRef.current?.connection.whenState('connected')

      const channel = clientRef.current?.channels.get(channelName!)
      try {
        await channel?.subscribe(REALTIME_EVENTS, (message) => {
          callbackRef.current({
            name: message.name! as RealtimeEventName,
            data: message.data
          })
        })

        channel?.on('detached', async () => {
          try {
            if (clientRef?.current?.connection.state === 'connected') {
              await channel.attach()
            }
          } catch (err) {
            console.error('Failed to reattach channel:', err)
          }
        })
      } catch (err) {
        console.error('Failed to setup realtime channel:', err)
      }
    }

    setupChannel()
  }, [clientRef, channelName])
}
